'use client';

import type { ProductDisplayData } from '@eliadmin/core/src/api/products/types/product-types';
import { AddToCartButton } from '@eliadmin/core/src/components/product/add-to-cart-button';
import { AddToFavoritesButton } from '@eliadmin/core/src/components/product/add-to-favorites-button';
import { useProductActionErrors } from '@eliadmin/core/src/components/nt/product/use-product-action-errors';
import { DialogProvider } from '@eliadmin/core/src/components/ui/dialog';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { VariantsProvider } from '@eliadmin/core/src/stores/variant-store';
import { isSellable } from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/*
 * Core buttons in the Figma look (hover state of card 46:4005): primary 48 px „Pridať do košíka“
 * with the plus icon and the white 48 px heart button. The cart button switches to its short label
 * („Do košíka“) below 200 px of its own width (core container query).
 */
const CART_BUTTON = 'w-full space-x-0 [&>span]:flex-initial';
const FAVORITE_BUTTON =
  'border-transparent px-0 shadow-float hover:border-[#CDD2CA] hover:shadow-none';
const FAVORITE_ICON =
  'text-[#0D110D] hover:text-[#0D110D] [[aria-pressed=true]_&]:fill-[#0D110D] [[aria-pressed=true]_&]:stroke-[#0D110D]';

/**
 * Add to cart and favourite through the core components (variant dialog, cart mutation, analytics,
 * „Pridané do košíka“ dialog, guest redirect), each card with its own variant store and dialog slot
 * like the core product card. Failed requests surface as an error toast.
 */
export function ProductTileActions({
  product,
  className,
}: {
  product: ProductDisplayData;
  className?: string;
}) {
  const teaser = useGlobalStore(state => state.templateSettings.product_teaser);
  useProductActionErrors(product);

  return (
    <VariantsProvider state={{ product, selectedQuantity: product.min_quantity ?? 1 }}>
      <DialogProvider>
        <div className={cn('flex items-center justify-end gap-3', className)}>
          {teaser?.cart_button?.enabled ? (
            <div className="min-w-0 flex-1 @container">
              <AddToCartButton
                product={product}
                quantity={1}
                sellable={isSellable(product, 1)}
                size="md"
                iconLeft="plus"
                className={CART_BUTTON}
              />
            </div>
          ) : null}
          {teaser?.favorite_icon?.enabled ? (
            <AddToFavoritesButton
              slug={product.slug}
              productId={product.id}
              isFavorite={product.is_favorite}
              buttonSize="icon"
              buttonClassName={FAVORITE_BUTTON}
              className={FAVORITE_ICON}
            />
          ) : null}
        </div>
      </DialogProvider>
    </VariantsProvider>
  );
}
